import React from 'react';
import { testimonials } from './homeData';

function TestimonialCard({ testimonial, featured = false }) {
  const initials = testimonial.name
    .split(' ')
    .map((part) => part[0])
    .join('');

  return (
    <article
      className={`min-h-[250px] rounded-xl border p-5 shadow-[0_10px_30px_rgba(42,32,27,0.09)] ${
        featured
          ? 'border-[#d88935] bg-[#d88935] text-white'
          : 'border-[#ebe5df] bg-white text-[#2a201b]'
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-sm font-semibold ${
            featured ? 'bg-white/20 text-white' : 'bg-[#f3e6d5] text-[#b86f25]'
          }`}
        >
          {initials}
        </div>
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold">{testimonial.name}</h3>
          <p className={`mt-0.5 text-[9px] leading-4 ${featured ? 'text-white/75' : 'text-[#837970]'}`}>
            {testimonial.role}
          </p>
        </div>
      </div>

      <div className={`mt-4 flex gap-1 text-base ${featured ? 'text-white' : 'text-[#e4a331]'}`} aria-label="5 out of 5 stars">
        {Array.from({ length: 5 }, (_, index) => (
          <span key={index} aria-hidden="true">&#9733;</span>
        ))}
      </div>

      <p className={`mt-3 text-[11px] leading-5 ${featured ? 'text-white/90' : 'text-[#5d544d]'}`}>
        {testimonial.text}
      </p>
    </article>
  );
}

function TestimonialTrack({ items, duration, delay = '0s', className = '' }) {
  return (
    <div className={`testimonial-column ${className}`}>
      <div
        className="testimonial-track"
        style={{ animationDuration: duration, animationDelay: delay }}
      >
        {[0, 1].map((copy) => (
          <div key={copy} className="space-y-5 pb-5" aria-hidden={copy === 1}>
            {items.map((testimonial, index) => (
              <TestimonialCard
                key={`${copy}-${testimonial.name}`}
                testimonial={testimonial}
                featured={testimonial.featured || index === 1}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function TestimonialsSection() {
  const secondColumn = [...testimonials.slice(1), testimonials[0]];
  const thirdColumn = [...testimonials.slice(2), ...testimonials.slice(0, 2)];

  return (
    <section className="bg-white px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
      <div className="mx-auto max-w-3xl text-center">
        <h2 className="text-2xl font-bold text-[#111111] sm:text-3xl lg:text-4xl">
          What Our Clients Say
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-xs leading-6 text-[#4e4741] sm:text-sm">
          Hear from businesses who have transformed their fleet operations with our innovative solutions.
        </p>
      </div>

      <div className="relative mx-auto mt-8 h-[560px] max-w-7xl overflow-hidden sm:mt-10 lg:h-[620px]">
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-24 bg-gradient-to-b from-white to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-24 bg-gradient-to-t from-white to-transparent" />

        <div className="grid h-full grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <TestimonialTrack items={testimonials} duration="28s" />
          <TestimonialTrack items={secondColumn} duration="32s" delay="-8s" className="hidden sm:block" />
          <TestimonialTrack items={thirdColumn} duration="30s" delay="-15s" className="hidden lg:block" />
        </div>
      </div>
    </section>
  );
}

export default TestimonialsSection;
